// src/services/api.js
//
// Centralized fetch wrapper for talking to the FastAPI backend.
// Automatically attaches the JWT access token (if present) and
// the JSON content-type header, and normalizes error handling.

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function formatErrorItem(item) {
  if (item === null || item === undefined) return null;
  if (typeof item === 'string') return item;
  if (Array.isArray(item)) {
    return item.map(formatErrorItem).filter(Boolean).join(' | ');
  }
  if (typeof item === 'object') {
    const message = item.msg || item.message || item.detail;
    const loc = item.loc;
    let location = '';

    if (Array.isArray(loc)) {
      const filtered = loc.filter((part) => part !== 'body' && part !== 'query' && part !== 'path' && part !== 'header');
      location = filtered.join('.') || loc.join('.');
    } else if (loc) {
      location = String(loc);
    }

    if (message) {
      return location ? `${location}: ${message}` : String(message);
    }

    return JSON.stringify(item);
  }
  return String(item);
}

function formatErrorDetail(detail) {
  if (detail === null || detail === undefined) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(formatErrorItem)
      .filter(Boolean)
      .join(' | ');
  }
  if (typeof detail === 'object') {
    return formatErrorItem(detail);
  }
  return String(detail);
}

async function request(path, { method = 'GET', body, auth = true, headers = {} } = {}) {
  const finalHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (auth) {
    const token = localStorage.getItem('access_token');
    if (token) {
      finalHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: finalHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await response.json();
  } catch (err) {
    data = null;
  }

  if (response.status === 401 && auth) {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken && path !== '/api/auth/refresh') {
      try {
        const refreshResponse = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          localStorage.setItem('access_token', refreshData.access_token);
          localStorage.setItem('refresh_token', refreshData.refresh_token);
          if (finalHeaders['Authorization']) {
            finalHeaders['Authorization'] = `Bearer ${refreshData.access_token}`;
          }

          const retryResponse = await fetch(`${API_BASE_URL}${path}`, {
            method,
            headers: finalHeaders,
            body: body ? JSON.stringify(body) : undefined,
          });

          let retryData = null;
          try {
            retryData = await retryResponse.json();
          } catch (err) {
            retryData = null;
          }

          if (retryResponse.ok) {
            return retryData;
          }

          const retryDetail = retryData && retryData.detail;
          const retryMessage =
            typeof retryDetail === 'string'
              ? retryDetail
              : formatErrorDetail(retryDetail) || 'Something went wrong. Please try again.';
          const retryError = new Error(retryMessage);
          retryError.status = retryResponse.status;
          retryError.data = retryData;
          throw retryError;
        }
      } catch (refreshErr) {
        // Fall through to handle as original 401 below.
      }
    }
  }

  if (!response.ok) {
    const detail = data && data.detail;
    const message =
      typeof detail === 'string'
        ? detail
        : formatErrorDetail(detail) || 'Something went wrong. Please try again.';
    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
};